import type { HeroKey } from '@/components/PageHero/heroImages';

/**
 * What's new - the one place a release's pop-up is defined. For the next release, bump
 * WHATS_NEW_VERSION (it must sort after the last one as plain text, e.g. "2026-12") and edit the cards.
 * Everyone whose account has not seen that version sees the pop-up once on their next signed-in visit.
 */
export const WHATS_NEW_VERSION = '2026-10';

export interface WhatsNewCard {
  id: string;
  title: string;
  body: string;
  /** Existing section scene from public/images/hero. */
  hero: HeroKey;
  /** Where "Take me there" goes. Leave out for no link. */
  to?: string;
  linkLabel?: string;
}

export const WHATS_NEW_CARDS: readonly WhatsNewCard[] = [
  {
    id: 'home',
    title: 'A new Home page',
    body: 'Home now shows where your hobby stands: what to paint next, recent games, your projects and what your friends have been up to. Pin the cards you like and hide the rest.',
    hero: 'home',
    to: '/home',
    linkLabel: 'See Home',
  },
  {
    id: 'journal',
    title: 'Painting journal and projects',
    body: 'Keep a journal of your painting with photos. Start a project with a target date and link the models that belong to it, then watch it come together.',
    hero: 'hobby',
    to: '/projects',
    linkLabel: 'See Projects',
  },
  {
    id: 'results',
    title: 'Log your game results',
    body: 'Played a game without the app open? Log the result afterwards and keep your record, your armies and your personal bests in one place.',
    hero: 'play',
    to: '/battles/log',
    linkLabel: 'Log a result',
  },
  {
    id: 'grimm',
    title: 'Grimm keeps getting better',
    body: 'Grimm has a bigger rules library and now starts from the game you play most. Ask away and it will answer for your game.',
    hero: 'grimm',
    to: '/grimm',
    linkLabel: 'Ask Grimm',
  },
];

/** The last card: clubs and events near you, with the optional town question. */
export const WHATS_NEW_TOWN = {
  title: 'Clubs and events near you',
  body: 'Find clubs, regular gaming nights and events close to you. Tell us your nearest town or city and Home and Near Me will show what is on nearby.',
  hero: 'events' as HeroKey,
  question: 'Where do you play?',
  privacy: 'We keep this rounded to about 1 km, use it only to suggest clubs and events nearby, and you can remove it any time in Settings.',
};

/** True when this account has not yet seen the given release. Versions compare as plain text. */
export const hasUnseenRelease = (seen: string | null | undefined, current: string = WHATS_NEW_VERSION): boolean =>
  !seen || seen < current;
